/**
 * Типы для сущности wishlist-item (желания в вишлистах)
 * @module entities/wishlist-item/model/types
 */

/**
 * Константный массив для enum типа GiftTag - SINGLE SOURCE OF TRUTH
 * Используется для вывода типов и для zod валидации
 */
export const GIFT_TAGS = ['none', 'really-want', 'would-be-nice', 'thinking', 'buy-myself'] as const;

/**
 * Метка подарка (gift tag)
 */
export type GiftTag = typeof GIFT_TAGS[number];

/**
 * Желание (item) в вишлисте
 */
export interface WishlistItem {
  id: string;
  title: string;
  price?: number;
  currency?: string;
  imageUrl?: string;
  imageUrls?: string[];
  link?: string;
  description?: string;
  isPurchased?: boolean;
  purchasedBy?: string;
  wishlistIds: string[];
  wishlistTitle?: string;
  giftTag?: GiftTag;
  category?: string[];
  purchaseLocation?: string;
  sourceItemId?: string;
  sourceCollectionItemId?: string;
  createdBy?: string;
  createdAt?: string; // ISO-дата создания желания
  // Populated данные (join)
  owner?: {
    id: string;
    username: string;
    fullName?: string;
    avatarUrl?: string;
  };
  creator?: {
    id: string;
    username: string;
    fullName?: string;
    avatarUrl?: string;
  };
}

/**
 * Данные для создания/обновления желания
 */
export interface WishlistItemInput {
  title: string;
  price?: number;
  currency?: string;
  imageUrl?: string;
  imageUrls?: string[];
  link?: string;
  description?: string;
  giftTag?: GiftTag;
  category?: string[];
  purchaseLocation?: string;
  /** ID оригинального желания из wishlist_items, если копия из чужого вишлиста */
  sourceItemId?: string;
  /** ID товара из collection_items, если копия из подборки */
  sourceCollectionItemId?: string;
}

/**
 * Данные для обновления желания (только изменяемые поля)
 */
export type WishlistItemUpdateData = Partial<WishlistItemInput> & {
  /** Помечено как купленное (обычно через toggle-мутацию) */
  isPurchased?: boolean;
  /** ID забронировавшего пользователя */
  purchasedBy?: string | null;
};

/**
 * Забронированный подарок с информацией о владельце
 */
export interface BookedItem {
  id: string;
  title: string;
  price: number | null;
  currency: string | null;
  imageUrl: string | null;
  wishlistId: string;
  /** Информация о владельце вишлиста */
  owner: {
    id: string;
    name: string;
    lastName: string;
    username: string;
    birthDate: string | null;
  };
  /** Дата события */
  eventDate: string | null;
}

/**
 * Популярное желание с данными вишлиста (для каталога)
 */
export type PopularItemWithWishlist = WishlistItem & {
  wishlist: {
    id: string;
    title: string;
    iconId: string | null;
  } | null;
  /** Количество раз, когда это желание было скопировано */
  copyCount: number;
  /** Добавлено ли желание текущим пользователем */
  isAddedByUser: boolean;
  /** Является ли желание собственным (из вишлистов текущего пользователя) */
  isOwnItem: boolean;
  /** ID подборки, если товар из collection_items (для навигации на страницу подборки) */
  collectionId?: string;
  /** Название подборки */
  collectionTitle?: string;
  /** Рекламный товар из подборки */
  isSponsored?: boolean;
};